import { PHASE_EXPORT, PHASE_PRODUCTION_BUILD, PHASE_PRODUCTION_SERVER, PHASE_DEVELOPMENT_SERVER, PHASE_TEST, PHASE_INFO } from 'next/constants';
import { z } from 'zod';

const variables = {
    NEXT_PUBLIC_DEFAULT_LOCALE: process.env.NEXT_PUBLIC_DEFAULT_LOCALE,
    NEXT_PUBLIC_PROTOCOL: process.env.NEXT_PUBLIC_PROTOCOL,
    NEXT_PUBLIC_SITE_NAME: process.env.NEXT_PUBLIC_SITE_NAME,
    NEXT_PUBLIC_SOCIAL_CREATOR_X: process.env.NEXT_PUBLIC_SOCIAL_CREATOR_X,
    NEXT_PUBLIC_SOCIAL_URL_FACEBOOK: process.env.NEXT_PUBLIC_SOCIAL_URL_FACEBOOK,
    NEXT_PUBLIC_SOCIAL_URL_INSTAGRAM: process.env.NEXT_PUBLIC_SOCIAL_URL_INSTAGRAM,
    NEXT_PUBLIC_SOCIAL_URL_LINKEDIN: process.env.NEXT_PUBLIC_SOCIAL_URL_LINKEDIN,
    NEXT_PUBLIC_SOCIAL_URL_PINTEREST: process.env.NEXT_PUBLIC_SOCIAL_URL_PINTEREST,
    NEXT_PUBLIC_SOCIAL_URL_TIKTOK: process.env.NEXT_PUBLIC_SOCIAL_URL_TIKTOK,
    NEXT_PUBLIC_SOCIAL_URL_X: process.env.NEXT_PUBLIC_SOCIAL_URL_X,
    NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,
    NEXT_PUBLIC_SITE_DOMAIN_PRODUCTION: process.env.NEXT_PUBLIC_SITE_DOMAIN_PRODUCTION,
    NODE_ENV: process.env.NODE_ENV,
    PHASE: process.env.PHASE
};
const publicVariablesSchema = z.object({
    // Defaults are provided so build-time evaluation won't fail when env vars
    // are not present (e.g. during local builds or CI without explicit env).
    NEXT_PUBLIC_DEFAULT_LOCALE: z.string().default("en-US"),
    NEXT_PUBLIC_PROTOCOL: z.union([
        z.literal("http"),
        z.literal("https")
    ]).default("https"),
    NEXT_PUBLIC_SITE_NAME: z.string(),
    NEXT_PUBLIC_SOCIAL_CREATOR_X: z.string().optional(),
    NEXT_PUBLIC_SOCIAL_URL_FACEBOOK: z.url().optional(),
    NEXT_PUBLIC_SOCIAL_URL_INSTAGRAM: z.url().optional(),
    NEXT_PUBLIC_SOCIAL_URL_LINKEDIN: z.url().optional(),
    NEXT_PUBLIC_SOCIAL_URL_PINTEREST: z.url().optional(),
    NEXT_PUBLIC_SOCIAL_URL_TIKTOK: z.url().optional(),
    NEXT_PUBLIC_SOCIAL_URL_X: z.url().optional(),
    NEXT_PUBLIC_VERCEL_URL: z.string().optional(),
    NEXT_PUBLIC_SITE_DOMAIN_PRODUCTION: z.string().default("localhost:3000"),
    NODE_ENV: z.enum([
        "development",
        "production",
        "test"
    ]).default("production"),
    PHASE: z.enum([
        PHASE_EXPORT,
        PHASE_PRODUCTION_BUILD,
        PHASE_PRODUCTION_SERVER,
        PHASE_DEVELOPMENT_SERVER,
        PHASE_TEST,
        PHASE_INFO
    ]).default(PHASE_PRODUCTION_BUILD)
});
const privateVariablesSchemaClient = z.object({
    ANALYZE: z.undefined()
});
const privateVariablesSchemaServer = z.object({
    ANALYZE: z.stringbool().optional()
});
const envSchema = z.intersection(publicVariablesSchema, typeof window === "undefined" ? privateVariablesSchemaServer : privateVariablesSchemaClient);
const env = envSchema.parse(variables);

const DEFAULT_OPTION = "Default Title";
const USER_AUTH_BASE_PATH = "/auth";
const USER_AUTH_SIGNIN_PATH = `${USER_AUTH_BASE_PATH}/sign-in`;
const USER_AUTH_SIGNUP_PATH = `${USER_AUTH_BASE_PATH}/sign-up`;
const HIDDEN_PRODUCT_TAG = "web-frontend-hidden";
const SITE_DOMAIN = [
    PHASE_PRODUCTION_BUILD,
    PHASE_PRODUCTION_SERVER
].includes(env.PHASE) ? env.NEXT_PUBLIC_VERCEL_URL : env.NEXT_PUBLIC_SITE_DOMAIN_PRODUCTION;
const SITE_URL_CURRENT = `${env.NEXT_PUBLIC_PROTOCOL}://${SITE_DOMAIN}`;
const SITE_URL_PRODUCTION = `${env.NEXT_PUBLIC_PROTOCOL}://${env.NEXT_PUBLIC_SITE_DOMAIN_PRODUCTION}`;
const COOKIE_CONSENT = "consent";
const PRE_GENERATED_BCP47_LANGUAGE_TAGS = [
    "ar-SA",
    "bn-BD",
    "bn-IN",
    "cs-CZ",
    "da-DK",
    "de-AT",
    "de-CH",
    "de-DE",
    "el-GR",
    "en-AU",
    "en-CA",
    "en-GB",
    "en-IE",
    "en-IN",
    "en-NZ",
    "en-US",
    "en-ZA",
    "es-AR",
    "es-CL",
    "es-CO",
    "es-ES",
    "es-MX",
    "es-US",
    "fi-FI",
    "fr-BE",
    "fr-CA",
    "fr-CH",
    "fr-FR",
    "he-IL",
    "hi-IN",
    "hu-HU",
    "id-ID",
    "it-CH",
    "it-IT",
    "ja-JP",
    "ko-KR",
    "nl-BE",
    "nl-NL",
    "no-NO",
    "pl-PL",
    "pt-BR",
    "pt-PT",
    "ro-RO",
    "ru-RU",
    "sk-SK",
    "sv-SE",
    "ta-IN",
    "ta-LK",
    "th-TH",
    "tr-TR",
    "zh-CN",
    "zh-HK",
    "zh-TW"
];
const SOCIAL_URLS = Object.keys(env).filter((key)=>typeof key === "string").filter((key)=>key.startsWith("NEXT_PUBLIC_SOCIAL_URL_")).map((key)=>env[key]).filter(Boolean).filter((url)=>typeof url === "string");

export { COOKIE_CONSENT, DEFAULT_OPTION, HIDDEN_PRODUCT_TAG, PRE_GENERATED_BCP47_LANGUAGE_TAGS, SITE_DOMAIN, SITE_URL_CURRENT, SITE_URL_PRODUCTION, SOCIAL_URLS, USER_AUTH_BASE_PATH, USER_AUTH_SIGNIN_PATH, USER_AUTH_SIGNUP_PATH };
